import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getTeamDeliverables } from '../../../services/deliverableService';

const ProgressTracker = ({ teamId }) => {
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (teamId) {
      loadDeliverables();
    }
  }, [teamId]);

  const loadDeliverables = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getTeamDeliverables(teamId);
      setDeliverables(data);
    } catch (err) {
      console.error('Error loading deliverables:', err);
      setError(err?.message || 'Failed to load deliverables');
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!deliverables?.length) return 0;
    const completedCount = deliverables?.filter(d => d?.status === 'completed')?.length;
    return Math.round((completedCount / deliverables?.length) * 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'in_progress': return 'text-warning bg-warning/10';
      case 'not_started': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in_progress': return 'Clock';
      case 'not_started': return 'Circle';
      default: return 'Circle';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString();
  };

  const isOverdue = (dueDate, status) => {
    return new Date(dueDate) < new Date() && status !== 'completed';
  };

  const overallProgress = calculateProgress();

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" size={24} className="animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading progress...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 text-error">
          <Icon name="AlertCircle" size={20} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Team Progress Tracker</h2>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-primary/5 rounded-lg text-center">
          <div className="text-2xl font-bold text-primary mb-1">
            {deliverables?.filter(d => d?.status === 'completed')?.length}
          </div>
          <div className="text-sm text-muted-foreground">Completed</div>
        </div>
        <div className="p-4 bg-warning/5 rounded-lg text-center">
          <div className="text-2xl font-bold text-warning mb-1">
            {deliverables?.filter(d => d?.status === 'in_progress')?.length}
          </div>
          <div className="text-sm text-muted-foreground">In Progress</div>
        </div>
        <div className="p-4 bg-error/5 rounded-lg text-center">
          <div className="text-2xl font-bold text-error mb-1">
            {deliverables?.filter(d => isOverdue(d?.dueDate, d?.status))?.length}
          </div>
          <div className="text-sm text-muted-foreground">Overdue</div>
        </div>
        <div className="p-4 bg-muted/50 rounded-lg text-center">
          <div className="text-2xl font-bold text-foreground mb-1">
            {deliverables?.length}
          </div>
          <div className="text-sm text-muted-foreground">Total</div>
        </div>
      </div>

      {/* Overall Progress */}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-foreground">Overall Progress</h3>
          <span className="text-2xl font-bold text-primary">{overallProgress}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-3 mb-2">
          <div 
            className="bg-primary h-3 rounded-full transition-all duration-500"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{deliverables?.filter(d => d?.status === 'completed')?.length} of {deliverables?.length} deliverables completed</span>
        </div>
      </div>

      {/* Deliverables List */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Deliverables</h3>
        {deliverables?.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
            <Icon name="FileText" size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No deliverables defined yet</p>
            <p className="text-sm text-muted-foreground mt-1">Contact your admin to add deliverables</p>
          </div>
        ) : (
          <div className="space-y-4">
            {deliverables?.map((deliverable, index) => {
              const overdue = isOverdue(deliverable?.dueDate, deliverable?.status);
              const status = overdue ? 'overdue' : deliverable?.status;
              return (
                <div key={deliverable?.id} className="flex items-start space-x-4">
                  <div className="flex flex-col items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                      overdue ? 'text-error bg-error/10' : getStatusColor(deliverable?.status)
                    }`}>
                      <Icon name={overdue ? 'AlertCircle' : getStatusIcon(deliverable?.status)} size={20} />
                    </div>
                    {index < deliverables?.length - 1 && (
                      <div className="w-px h-12 bg-border mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-md font-medium text-foreground">{deliverable?.deliverableName}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        overdue ? 'text-error bg-error/10' : getStatusColor(deliverable?.status)
                      }`}>
                        {overdue ? 'Overdue' : deliverable?.status?.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm mb-2">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={14} className="text-muted-foreground" />
                        <span className={overdue ? 'text-error' : 'text-muted-foreground'}>
                          Due: {formatDate(deliverable?.dueDate)}
                          {overdue && ' (Overdue)'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Upload" size={14} className="text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {deliverable?.completedUploadCount} / {deliverable?.requiredUploadCount} files uploaded
                        </span>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          deliverable?.status === 'completed' ? 'bg-success' : 'bg-primary'
                        }`}
                        style={{ 
                          width: `${Math.min((deliverable?.completedUploadCount / deliverable?.requiredUploadCount) * 100, 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressTracker;